import React, { Suspense, useEffect, useMemo, useState, useRef } from 'react';
import PageConfig from '@/component/layout/PageConfig.tsx';
import { useNavigate, useParams } from 'react-router-dom';
import CloseButton from '@/component/button/CloseButton.tsx';
import { GoogleMap, MarkerF, InfoWindowF, CircleF, PolylineF } from '@react-google-maps/api';
import useQueryGetUserDetail from '@/api/auth/getUser.ts';
import { IMarker, IMarkerInfo, MarketTYPE } from '@/pages/dashboard/DashboardMapDefault.tsx';
import { AlertStatus } from '@/api/user/getUsers.ts';
import useIsMount from '@/util/hook/useIsMount.ts';
import ReactDOMServer from 'react-dom/server';
import MarkerWindow from '@/pages/dashboard/MarkerWindow.tsx';
import HomeIcon from '@/assets/ic_map_house.svg?react';
import useQueryGetMe from '@/api/auth/getMe.ts';
import useQueryGetUserDailyPosition, { IUserPosition } from '@/api/user/getUserDailyPosition.ts';

export interface IProps {}

const UserDetailMapPage: React.FC<IProps> = ({}) => {
    const params = useParams();
    const userID = Number(params?.id);
    const navigate = useNavigate();
    const { data } = useQueryGetUserDetail(userID);
    const { data: me } = useQueryGetMe();
    const { data: dailyPositions, isLoading: isLoadingPositions, isError, error, refetch: refetchPositions } = useQueryGetUserDailyPosition(userID);
    const [userInfo, setUserInfo] = useState<any>(null);
    const { isMount } = useIsMount();
    const [showDailyTrack, setShowDailyTrack] = useState<boolean>(true);
    const [positionsErrorMsg, setPositionsErrorMsg] = useState<string>("데이터 없음");
    const [selectedPosition, setSelectedPosition] = useState<IUserPosition | null>(null);
    
    // 위치 재생 관련 상태
    const [isPlaying, setIsPlaying] = useState<boolean>(false);
    const [currentPositionIndex, setCurrentPositionIndex] = useState<number>(0);
    const [playbackSpeed, setPlaybackSpeed] = useState<number>(1); // 재생 속도 (1x, 2x, 4x)
    const playbackTimerRef = useRef<number | null>(null);
    const [sortedPositions, setSortedPositions] = useState<IUserPosition[]>([]);

    // 일일 위치 데이터 처리 및 정렬된 위치 데이터 설정
    useEffect(() => {
        if (isLoadingPositions) {
            setPositionsErrorMsg("");
        } else if (isError) {
            setPositionsErrorMsg("위치 데이터 조회 중 오류가 발생했습니다.");
        } else if (dailyPositions) {
            if (dailyPositions.length === 0) {
                setPositionsErrorMsg("오늘 기록된 위치 데이터가 없습니다.");
                setSortedPositions([]);
            } else {
                setPositionsErrorMsg("");
                // 시간순으로 정렬된 위치 데이터 설정
                const sorted = [...dailyPositions].sort((a, b) => {
                    return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
                });
                setSortedPositions(sorted);
                // 재생 중단 및 인덱스 초기화
                setIsPlaying(false);
                setCurrentPositionIndex(0);
            }
        } else {
            setPositionsErrorMsg("위치 데이터가 없습니다.");
            setSortedPositions([]);
        }
    }, [dailyPositions, isLoadingPositions, isError, error]);

    // 재생 속도에 따른 지연 시간 계산 (밀리초)
    const getDelayBySpeed = (speed: number): number => {
        switch (speed) {
            case 1: return 1000;  // 1x = 1초
            case 2: return 500;   // 2x = 0.5초
            case 4: return 250;   // 4x = 0.25초
            default: return 1000; // 기본 1초
        }
    };

    // 재생 시작 함수
    const startPlayback = () => {
        if (sortedPositions.length === 0) return;
        
        // 이미 재생 중이면 중지
        if (isPlaying) {
            stopPlayback();
            return;
        }
        
        // 재생 시작
        setIsPlaying(true);
        
        // 마지막 위치라면 처음으로 돌아가기
        if (currentPositionIndex >= sortedPositions.length - 1) {
            setCurrentPositionIndex(0);
        }
        
        // 타이머 시작
        playNext();
    };
    
    // 다음 위치로 이동하는 함수
    const playNext = () => {
        if (playbackTimerRef.current) {
            window.clearTimeout(playbackTimerRef.current);
        }
        
        if (currentPositionIndex < sortedPositions.length - 1) {
            // 속도에 따른 고정 지연 시간 사용
            const delay = getDelayBySpeed(playbackSpeed);
            
            // 다음 위치로 이동
            playbackTimerRef.current = window.setTimeout(() => {
                setCurrentPositionIndex(prevIndex => prevIndex + 1);
                
                // 마지막 위치가 아니면 계속 재생
                if (currentPositionIndex < sortedPositions.length - 2) {
                    playNext();
                } else {
                    // 마지막 위치에 도달하면 재생 중단
                    setIsPlaying(false);
                }
            }, delay);
        } else {
            // 마지막 위치에 도달하면 재생 중단
            setIsPlaying(false);
        }
    };
    
    // 재생 중지 함수
    const stopPlayback = () => {
        setIsPlaying(false);
        if (playbackTimerRef.current) {
            window.clearTimeout(playbackTimerRef.current);
            playbackTimerRef.current = null;
        }
    };
    
    // 재생 속도 변경 함수
    const changePlaybackSpeed = () => {
        // 1x -> 2x -> 4x -> 1x 순환
        setPlaybackSpeed(prevSpeed => {
            const newSpeed = prevSpeed === 4 ? 1 : prevSpeed * 2;
            
            // 재생 중이라면 현재 재생 중단 후 새 속도로 재시작
            if (isPlaying) {
                stopPlayback();
                setTimeout(() => {
                    setIsPlaying(true);
                    playNext();
                }, 100);
            }
            
            return newSpeed;
        });
    };
    
    // 컴포넌트 언마운트 시 타이머 정리
    useEffect(() => {
        return () => {
            if (playbackTimerRef.current) {
                window.clearTimeout(playbackTimerRef.current);
            }
        };
    }, []);
    
    // 재생 상태가 변경될 때 타이머 업데이트
    useEffect(() => {
        if (isPlaying) {
            playNext();
        } else if (playbackTimerRef.current) {
            window.clearTimeout(playbackTimerRef.current);
            playbackTimerRef.current = null;
        }
    }, [isPlaying]);

    const openUserInfo = (info?: IMarker) => {
        if (!info || userInfo?.id === info?.id) {
            setUserInfo(undefined);
        } else {
            const { latitude = 0, longitude = 0 } = info?.location || {};
            const isMasking = me?.email === 'dbd_cm@clomental.com';

            if (latitude === 0 && longitude === 0) {
                const infoWithoutAddress: IMarkerInfo = {
                    ...info!,
                    // locationName: 'No location',
                    address: 'Coordinates not available',
                };

                infoWithoutAddress.render = ReactDOMServer.renderToString(<MarkerWindow info={infoWithoutAddress} isMasking={isMasking} />);
                setUserInfo(infoWithoutAddress);
                return;
            }

            const geocoder = new google.maps.Geocoder();
            geocoder.geocode(
                { location: { lat: latitude, lng: longitude } },
                (results, status) => {
                    if (status === 'OK' && results && results[0]) {
                        const infoWithAddress: IMarkerInfo = {
                            ...info!,
                            // locationName: results[1]?.formatted_address || '',
                            address: results[0].formatted_address,
                        };

                        infoWithAddress.render = ReactDOMServer.renderToString(<MarkerWindow info={infoWithAddress} isMasking={isMasking} />);
                        setUserInfo(infoWithAddress);
                    } else {
                        const infoWithoutAddress: IMarkerInfo = {
                            ...info!,
                            // locationName: 'Unknown location',
                            address: 'Geocoding failed',
                        };

                        infoWithoutAddress.render = ReactDOMServer.renderToString(<MarkerWindow info={infoWithoutAddress} isMasking={isMasking} />);
                        setUserInfo(infoWithoutAddress);
                    }
                }
            );
        }
    };

    // 위치 포인트 클릭 핸들러
    const handlePositionClick = (position: IUserPosition) => {
        setSelectedPosition(position);
    };

    // 시간 포매팅 함수
    const formatTimestamp = (timestamp: string) => {
        try {
            const date = new Date(timestamp);
            return date.toLocaleString('ko-KR', { 
                year: 'numeric', 
                month: '2-digit', 
                day: '2-digit', 
                hour: '2-digit', 
                minute: '2-digit', 
                second: '2-digit',
                hour12: true 
            });
        } catch (error) {
            return timestamp;
        }
    };

    const getMarkerColor = (alertStatus: AlertStatus | undefined) => {
        let color = '#2966F4'; // 기본값
        switch (alertStatus) {
            case AlertStatus.EMERGENCY:
                color = '#FF003D';
                break;
            case AlertStatus.BIO:
                color = '#FF7D33';
                break;
            default:
                color = '#2966F4';
                break;
        }
        return color;
    };

    const marker = useMemo(() => {
        if (!data) return undefined;
        return {
            id: Number(params?.id),
            deviceStatus: data?.device?.status,
            location: {
                latitude: data?.location?.latitude,
                longitude: data?.location?.longitude,
                accuracy: data?.location?.accuracy,
                timestamp: data?.location?.timestamp,
            },
            type: MarketTYPE.SINGLE,
            alertStatus: data?.alertStatus as AlertStatus ?? AlertStatus.NONE, // `AlertStatus` Enum으로 변환
            users: [data],
        };
    }, [data]);

    // 현재 재생 중인 위치
    const currentPlaybackPosition = useMemo(() => {
        return isPlaying && sortedPositions.length > 0 && currentPositionIndex < sortedPositions.length
            ? sortedPositions[currentPositionIndex]
            : null;
    }, [isPlaying, sortedPositions, currentPositionIndex]);

    const dailyPositionPath = useMemo(() => {
        if (sortedPositions.length === 0) return [];
        
        return sortedPositions.map(pos => ({
            lat: pos.latitude,
            lng: pos.longitude
        }));
    }, [sortedPositions]);

    // 위치 경로 스타일 
    const polylineOptions = {
        strokeColor: '#2966F4',
        strokeOpacity: 0.8,
        strokeWeight: 3,
    };

    useEffect(() => {
        if (!userInfo) return;

        const contentWithFont = `
            <div style="font-family: 'Noto Sans KR', sans-serif; font-weight: 400;">
                ${userInfo?.render}
            </div>
        `;

        const infoWindowInstance = new google.maps.InfoWindow({
            content: contentWithFont,
        });

        infoWindowInstance.setPosition({ lat: userInfo?.location?.latitude || 0, lng: userInfo?.location?.longitude || 0 });
        infoWindowInstance.open();
    }, [userInfo]);

    const mapOptions = {
        // disableDefaultUI: true,  // 기본 UI 비활성화
        // zoomControl: false,      // 줌 컨트롤 비활성화
        // mapTypeControl: false,   // 지도 유형 선택 비활성화
        // streetViewControl: false, // 스트리트 뷰 컨트롤 비활성화
        fullscreenControl: false // 풀스크린 컨트롤 비활성화
    };

    return (
        <PageConfig showTopNav={true} showSideNav={true}>
            <div className={'w-full h-full relative'}>
                <Suspense fallback={null}>
                    {isMount && data && (
                        <div style={{ width: '100%', height: '100%' }}>
                            <GoogleMap
                                center={{ lat: data?.location?.latitude || 0, lng: data?.location?.longitude || 0 }}
                                zoom={15}
                                mapContainerStyle={{ width: '100%', height: '100%' }}
                                options={mapOptions} // mapOptions 추가
                            >
                                {isMount && data?.location && (
                                    <>
                                        <CircleF
                                            center={{ lat: data?.location?.latitude || 0, lng: data?.location?.longitude || 0 }}
                                            radius={data?.location.accuracy}
                                            options={{
                                                fillColor: '#78bfffcc',
                                                strokeColor: '#78bfffcc',
                                                strokeWeight: 1,
                                            }}
                                        />
                                        <MarkerF
                                            position={{ lat: data?.location?.latitude || 0, lng: data?.location?.longitude || 0 }}
                                            icon={{
                                                url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
                                                    `
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 50 50">
                                                        <circle cx="25" cy="25" r="20" fill="${getMarkerColor(data?.alertStatus)}" stroke="white" stroke-width="3"/>
                                                    </svg>
                                                    `
                                                )}`,
                                                scaledSize: new google.maps.Size(40, 40),
                                            }}
                                            onClick={() => openUserInfo(marker)}
                                        />

                                        {isMount && data?.addressLatitude && data?.addressLongitude && (
                                            <MarkerF
                                                position={{ lat: data?.addressLatitude ?? 0, lng: data?.addressLongitude ?? 0 }}
                                                icon={{
                                                    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
                                                        ReactDOMServer.renderToString(<HomeIcon />)
                                                    )}`,
                                                    scaledSize: new google.maps.Size(25, 25),
                                                }}
                                            />
                                        )}

                                        {/* 하루 동안의 위치 경로 표시 */}
                                        {showDailyTrack && dailyPositionPath.length > 1 && (
                                            <PolylineF
                                                path={dailyPositionPath}
                                                options={polylineOptions}
                                            />
                                        )}

                                        {/* 위치 이동 경로의 각 포인트 표시 */}
                                        {showDailyTrack && !isPlaying && sortedPositions.map((pos, index) => (
                                            <MarkerF
                                                key={`daily-pos-${index}`}
                                                position={{ lat: pos.latitude, lng: pos.longitude }}
                                                icon={{
                                                    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
                                                        `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
                                                            <circle cx="8" cy="8" r="6" fill="#2966F4" stroke="white" stroke-width="2"/>
                                                        </svg>`
                                                    )}`,
                                                    scaledSize: new google.maps.Size(12, 12),
                                                }}
                                                opacity={0.8}
                                                onClick={() => handlePositionClick(pos)}
                                            />
                                        ))}
                                        
                                        {/* 재생 중인 위치 마커 (크고 눈에 띄게) */}
                                        {currentPlaybackPosition && (
                                            <MarkerF
                                                key="playback-marker"
                                                position={{ lat: currentPlaybackPosition.latitude, lng: currentPlaybackPosition.longitude }}
                                                icon={{
                                                    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
                                                        `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30">
                                                            <circle cx="15" cy="15" r="10" fill="#FF5722" stroke="white" stroke-width="3"/>
                                                        </svg>`
                                                    )}`,
                                                    scaledSize: new google.maps.Size(30, 30),
                                                }}
                                                animation={google.maps.Animation.BOUNCE}
                                            />
                                        )}
                                    </>
                                )}

                                {userInfo && (
                                    <InfoWindowF
                                        position={{ lat: userInfo?.location?.latitude || 0, lng: userInfo?.location?.longitude || 0 }}
                                        onCloseClick={() => setUserInfo(undefined)}
                                    >
                                        <div
                                            style={{ fontFamily: "'Noto Sans KR', sans-serif", fontWeight: 400 }}
                                            dangerouslySetInnerHTML={{ __html: userInfo?.render }}
                                        />
                                    </InfoWindowF>
                                )}

                                {/* 선택된 위치 포인트 정보창 */}
                                {selectedPosition && (
                                    <InfoWindowF
                                        position={{ lat: selectedPosition.latitude, lng: selectedPosition.longitude }}
                                        onCloseClick={() => setSelectedPosition(null)}
                                    >
                                        <div style={{ fontFamily: "'Noto Sans KR', sans-serif", fontWeight: 400 }}>
                                            <p className="font-semibold mb-1">위치 정보</p>
                                            <p className="text-sm">시간: {formatTimestamp(selectedPosition.timestamp)}</p>
                                            <p className="text-sm">정확도: {selectedPosition.accuracy}m</p>
                                            <p className="text-sm">순서: {selectedPosition.order}</p>
                                        </div>
                                    </InfoWindowF>
                                )}
                                
                                {/* 현재 재생 중인 위치 정보창 */}
                                {currentPlaybackPosition && !selectedPosition && (
                                    <InfoWindowF
                                        position={{ lat: currentPlaybackPosition.latitude, lng: currentPlaybackPosition.longitude }}
                                    >
                                        <div style={{ fontFamily: "'Noto Sans KR', sans-serif", fontWeight: 400 }}>
                                            <p className="font-semibold mb-1">현재 위치</p>
                                            <p className="text-sm">시간: {formatTimestamp(currentPlaybackPosition.timestamp)}</p>
                                            <p className="text-sm">정확도: {currentPlaybackPosition.accuracy}m</p>
                                            <p className="text-sm">진행: {currentPositionIndex + 1}/{sortedPositions.length}</p>
                                        </div>
                                    </InfoWindowF>
                                )}
                            </GoogleMap>
                        </div>
                    )}
                </Suspense>
                <CloseButton className={'absolute top-0 right-0 p-[20px] bg-white m-[20px]'} onClick={() => navigate(-1)} />
                
                {/* 위치 데이터 상태 표시 */}
                {isLoadingPositions && (
                    <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-white p-2 rounded shadow z-10">
                        위치 데이터 로딩 중...
                    </div>
                )}
                
                {!isLoadingPositions && positionsErrorMsg && (
                    <div className="absolute top-16 left-1/2 transform -translate-x-1/2 bg-white p-2 rounded shadow z-10 flex flex-col items-center">
                        <div>{positionsErrorMsg}</div>
                        <button 
                            className="mt-2 bg-blue-500 text-white px-3 py-1 rounded text-sm"
                            onClick={() => refetchPositions()}
                        >
                            다시 시도
                        </button>
                    </div>
                )}
                
                {/* 상단 중앙 버튼 그룹 */}
                <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-10 flex space-x-2">
                    {/* 위치 데이터 새로고침 버튼 */}
                    <button 
                        className="bg-blue-500 text-white px-3 py-2 rounded-lg shadow-md flex items-center justify-center"
                        onClick={() => refetchPositions()}
                        title="위치 데이터 새로고침"
                    >
                        새로고침
                    </button>
                    
                    {/* 재생 컨트롤 */}
                    {!isLoadingPositions && sortedPositions.length > 0 && (
                        <>
                            <button 
                                className={`${isPlaying ? 'bg-red-500' : 'bg-green-500'} text-white px-3 py-2 rounded-lg shadow-md flex items-center justify-center`}
                                onClick={startPlayback}
                                title={isPlaying ? "일시정지" : "재생"}
                            >
                                {isPlaying ? "정지" : "재생"}
                            </button>
                            
                            <button
                                className="bg-gray-200 px-3 py-2 rounded-lg shadow-md flex items-center justify-center"
                                onClick={changePlaybackSpeed}
                                title="재생 속도 변경"
                            >
                                {playbackSpeed}x
                            </button>
                            
                            {currentPlaybackPosition && (
                                <div className="bg-white px-3 py-2 rounded-lg shadow-md flex items-center justify-center">
                                    {currentPositionIndex + 1}/{sortedPositions.length}
                                </div>
                            )}
                        </>
                    )}
                    
                    {/* 일일 위치 경로 토글 버튼 */}
                    {!isLoadingPositions && dailyPositions && dailyPositions.length > 0 && (
                        <button 
                            className={`${showDailyTrack ? 'bg-blue-500 text-white' : 'bg-gray-300 text-gray-700'} px-3 py-2 rounded-lg shadow-md flex items-center justify-center`}
                            onClick={() => setShowDailyTrack(!showDailyTrack)}
                            title={showDailyTrack ? "경로 숨기기" : "경로 표시하기"}
                        >
                            {showDailyTrack ? "경로 숨기기" : "경로 표시"}
                        </button>
                    )}
                </div>
            </div>
        </PageConfig>
    );
};

export default UserDetailMapPage;